import React, { useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';

import config from '../config';
import loadMockData from './loadMockData';
import useFlags from './useFlags';
import { useAuth0 } from './auth0';

const api = axios.create({ baseURL: config?.mulesoft?.apiUrl });
const serverApi = axios.create();

const artificialDelay = (status, data) =>
  new Promise((resolve) => {
    setTimeout(() => resolve({ status, data }), 400);
  });

export const useApi = () => {
  const { getTokenSilently, loginWithRedirect } = useAuth0();
  const { mockPolicies, mockClaims, mockPayments } = useFlags();
  const getQaHeader = () =>
    config.appEnv === 'QA'
      ? { envIndicator: config?.mulesoft?.envIndicator || 'qa' }
      : {};

  const getServerApiConfig = (apiConfig = {}) => ({
    ...apiConfig,
    headers: {
      ...apiConfig?.headers,
      ...getQaHeader(),
    },
  });

  useEffect(() => {
    api.interceptors.response.use(
      (response) => response,
      (error) => {
        const { response } = error;
        if (!!response && response.status === 401) {
          loginWithRedirect();
        }
        return Promise.reject(error);
      },
    );
  }, [loginWithRedirect]);

  const getHeaders = useCallback(
    async (authenticated, apiConfig = {}) => {
      const headers = { ...apiConfig?.headers, ...getQaHeader() };
      if (authenticated) {
        const token = await getTokenSilently();

        if (!token) {
          loginWithRedirect();
        }
        headers.Authorization = `Bearer ${token.__raw}`;
      }
      return headers;
    },
    [getTokenSilently, loginWithRedirect],
  );

  const getUrl = (url, authenticated) =>
    authenticated ? `/secure${url}` : url;

  const apiFactory = useCallback(
    (authenticated) => {
      const get = async (path, apiConfig) => {
        const url = getUrl(path, authenticated);
        const headers = await getHeaders(authenticated, apiConfig);
        return api.get(url, { ...apiConfig, headers });
      };

      return {
        getClaims: async (policyNumber, apiConfig = {}) => {
          if (mockClaims) {
            const mockData = await loadMockData();

            if (mockData) {
              const policy = mockData.policiesMock.find(
                (c) => c.policyNumber === policyNumber,
              );

              if (policy.lineType) {
                const claims = mockData.claimsMock
                  .filter((c) => c.policyNumber === policyNumber)
                  .map((c) => {
                    return {
                      ...c,
                      policyNumber: c.policyNumber,
                      isFusion: Boolean(policy.lineType),
                      type: c.policyNumber.endsWith('N')
                        ? 'Homeowners Surplus'
                        : 'Hurricane Only',
                    };
                  });
                return artificialDelay(200, claims);
              }

              const claims = mockData.claimsMock.filter(
                (c) => c.policyNumber === policyNumber,
              );
              return artificialDelay(200, claims);
            }
          }
          return get(`/claims?policy_number=${policyNumber}`, apiConfig);
        },
        getPayments: async (policyNumber, apiConfig = {}) => {
          if (mockPayments) {
            const mockData = await loadMockData();
            if (mockData) {
              const payments = mockData.paymentsMock.filter(
                (p) => p.policyNumber === policyNumber,
              );
              return artificialDelay(200, { payments });
            }
          }

          return get(`/policies/${policyNumber}/payments`, apiConfig);
        },
        getPolicy: async (policyNumber, apiConfig = {}) => {
          if (mockPolicies) {
            const mockData = await loadMockData();
            if (mockData) {
              const policy = mockData.policiesMock.find(
                (x) => x.policyNumber === policyNumber,
              );
              return artificialDelay(200, policy);
            }
          }

          return get(`/policies/${policyNumber}`, apiConfig);
        },
        getPolicyPayments: async (policyNumber, lastName) => {
          if (mockPolicies && process.env.NODE_ENV !== 'production') {
            const policy = {
              policySystem: 'Violet',
              policyNumber: 'H3FL123456789',
              billing: {
                accountId: 208967,
                payorPartyId: '08DD3C89B01D1A99',
                currentDueAmount: 100,
                pastDueAmount: 3149,
                outstandingBalance: 3149,
              },
              insuredFirstName: 'Greatest',
              insuredLastName: 'Ever',
              insuredStreet: '4020 Glen Garry Rd W',
              insuredCity: 'Lakeland',
              insuredState: 'FL',
              insuredZip: '33813',
            };
            return artificialDelay(200, policy);
          }

          return serverApi.get('/api/policy-payments', {
            params: { policyNumber, lastName },
            ...getServerApiConfig(),
          });
        },
        getAllPolicies: async (policyNumber, apiConfig = {}) => {
          if (mockPolicies) {
            const mockData = await loadMockData();
            if (mockData) {
              return artificialDelay(200, mockData.policiesMock);
            }
          }

          return get('/policies', apiConfig);
        },
        getPolicyDeclaration: async (policyNumber, apiConfig) => {
          if (mockPolicies) {
            const mockData = await loadMockData();
            if (mockData) {
              return artificialDelay(200, mockData.documentsMock);
            }
          }

          return get(`/policies/${policyNumber}/attachments`, apiConfig);
        },
        getPolicyDocument: async (policyNumber, policyDocument) => {
          const configAttach = {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/pdf',
            },
          };
          const headersAttach = await getHeaders(authenticated, configAttach);

          const response = await api.get(
            getUrl(
              `/policies/${policyNumber}/attachments/${policyDocument.id}?source=${policyDocument.source}`,
              authenticated,
            ),
            { headers: { ...headersAttach } },
          );
          const fileUrl = response?.request?.responseURL;
          const a = document.createElement('a');
          a.href = fileUrl;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        },
        signUp: (body) =>
          serverApi.post('/api/sign-up', body, getServerApiConfig()),
        post: async (url, body, apiConfig) => {
          const headers = await getHeaders(authenticated, apiConfig);
          return api.post(getUrl(url, authenticated), body, {
            ...apiConfig,
            headers,
          });
        },
        postPolicyEndorsements: async (policyIdentifier, body, apiConfig) => {
          const headers = await getHeaders(authenticated, apiConfig);
          return api.post(
            getUrl(`/policies/${policyIdentifier}/endorsements`, authenticated),
            body,
            { ...apiConfig, headers },
          );
        },
        postLinkPolicy: async (body, apiConfig) => {
          const headers = await getHeaders(authenticated, apiConfig);

          return api.post(getUrl('/identity/policy', authenticated), body, {
            ...apiConfig,
            headers,
          });
        },
        patch: async (url, body, apiConfig) => {
          const headers = await getHeaders(authenticated, apiConfig);
          return api.patch(getUrl(url, authenticated), body, {
            ...apiConfig,
            headers,
          });
        },
        getVioletHealthCheck: () =>
          serverApi.get('/api/health-check-violet', getServerApiConfig()),
        getIPXHealthCheck: () =>
          serverApi.get('/api/health-check-ipx', getServerApiConfig()),
        validateEmailWithBriteVerify: async (email) => {
          if (!email) return null;
          try {
            const res = await axios.post(
              'https://bpi.briteverify.com/api/public/v1/fullverify',
              { email },
              {
                headers: {
                  Authorization: `ApiKey: ${config?.briteVerify?.apiKey}`,
                  'Content-Type': 'application/json',
                },
              },
            );
            return res.data;
          } catch (err) {
            console.error('Error validating email with BriteVerify:', err);
            return null;
          }
        },

        resendVerificationEmail: async (email) => {
          if (!email) return;

          const normalizedEmail = email.replace(' ', '+');
          return serverApi.post(
            '/api/email-verification',
            { email: normalizedEmail },
            getServerApiConfig(),
          );
        },
      };
    },
    [getHeaders, mockClaims, mockPayments, mockPolicies],
  );

  const ctx = useMemo(
    () => ({
      withAuth: apiFactory(true),
      ...apiFactory(false),
    }),
    [apiFactory],
  );

  return ctx;
};

export default function withApi(WrappedComponent) {
  return (props) => {
    const _api = useApi();
    return <WrappedComponent api={_api} {...props} />;
  };
}
